import { useToast } from '@helix-ds/react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { track } from '../../../utils/analytics'
import { withSupportMessage } from '../../../utils/errorMessage'
import { useFamilyPortalApiClient } from '../../auth/hooks/useFamilyPortalApiClient'
import {
  subjectRows as buildSubjectRows,
  scopedStateKey,
  type ConsentSubject,
  type SubjectRow,
} from '../consentSubjects'
import {
  answersFromSecondaryState,
  buildSecondaryDocumentContent,
  buildSecondaryElectionEntries,
  buildSubjectSecondaryElectionEntries,
  standingPersonSelectionKeys,
  type SecondaryElectionEntry,
} from './consentOptionsUtils'
import type {
  ConsentConfigReference,
  ConsentDocumentContent,
  SecondaryConsentRow,
  SecondaryConsentStateResponse,
  SecondaryElection,
} from './types'

export function useSecondaryConsent(
  consentConfig: ConsentConfigReference | null,
  locale: string,
  clientConfigVersion?: number | null
) {
  const apiClient = useFamilyPortalApiClient()
  const intl = useIntl()
  const { addNotification } = useToast()
  const [toggles, setToggles] = useState<Record<string, boolean>>({})
  const [initialToggles, setInitialToggles] = useState<Record<string, boolean>>({})
  const [loadedElections, setLoadedElections] = useState<Record<string, SecondaryElection>>({})
  const [standingPersonSelections, setStandingPersonSelections] = useState<ReadonlySet<string>>(() => new Set())
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!consentConfig) {
      setToggles({})
      setInitialToggles({})
      setLoadedElections({})
      setStandingPersonSelections(new Set())
      return
    }

    setToggles({})
    setInitialToggles({})
    setLoadedElections({})
    setStandingPersonSelections(new Set())

    const params = new URLSearchParams({
      configId: consentConfig.configId,
      configVersionId: consentConfig.configVersionId,
      consentType: 'secondary',
    })
    let cancelled = false
    apiClient
      .get(`/consents/secondary?${params.toString()}`)
      .then((res) => {
        if (cancelled || !res.ok) return
        return res.json()
      })
      .then((data: SecondaryConsentStateResponse | undefined) => {
        if (cancelled) return
        const { toggles: next, elections } = answersFromSecondaryState(data, null, {
          scopedElectionsAuthoritative: true,
        })
        setToggles(next)
        setInitialToggles(next)
        setLoadedElections(elections)
        setStandingPersonSelections(standingPersonSelectionKeys(data))
      })
      .catch(() => {
        // Existing choices are optional context; leave the flow usable if loading them fails.
      })
    return () => {
      cancelled = true
    }
  }, [apiClient, consentConfig, locale])

  function notifySaveFailed(message: string) {
    addNotification({
      type: 'error',
      title: intl.formatMessage({
        id: 'consentOptions.toast.couldNotSavePreferenceTitle',
        defaultMessage: "Couldn't save preference",
      }),
      message: withSupportMessage(intl, message),
    })
  }

  function couldNotSaveMessage(): string {
    return intl.formatMessage({
      id: 'consentOptions.error.couldNotSavePreference',
      defaultMessage: "We couldn't save your preference. Please try again.",
    })
  }

  function failureMessage(data: { detail?: unknown }, status: number): string {
    if (typeof data.detail === 'string') return data.detail
    if (status === 404) {
      return intl.formatMessage({
        id: 'consentOptions.error.noConsentContext',
        defaultMessage: 'No consent context found. Please refresh and try again.',
      })
    }
    return couldNotSaveMessage()
  }

  async function postSecondaryBatch(
    groups: readonly {
      entries: readonly SecondaryElectionEntry[]
      documentContent: ConsentDocumentContent
    }[]
  ): Promise<boolean> {
    if (!consentConfig) return false
    const analytics = { type: 'secondary_batch', item_count: groups.length }
    try {
      const res = await apiClient.post('/consents/secondary', {
        configId: consentConfig.configId,
        configVersionId: consentConfig.configVersionId,
        ...(typeof clientConfigVersion === 'number' ? { version: String(clientConfigVersion) } : {}),
        consentType: 'secondary',
        groups: groups.map(({ entries, documentContent }) => ({
          entries,
          documentContent,
        })),
      })
      if (res.ok) return true
      const data = (await res.json().catch(() => ({}))) as { detail?: unknown }
      track('consent.submission_failed', { ...analytics, status: res.status })
      notifySaveFailed(failureMessage(data, res.status))
      return false
    } catch {
      track('consent.submission_failed', { ...analytics, status: 'network_error' })
      notifySaveFailed(couldNotSaveMessage())
      return false
    }
  }

  function trackSavedItem(
    itemId: string,
    entries: readonly SecondaryElectionEntry[],
    rows: readonly SubjectRow[] | null
  ) {
    if (rows) {
      track('consent.secondary_decision', {
        item_id: itemId,
        subject_count: rows.length,
        accepted_count: entries.filter(({ election }) => election === 'opt_in').length,
        is_update: rows.some(({ key }) => initialToggles[key] !== undefined),
      })
      return
    }

    for (const entry of entries) {
      const key = scopedStateKey(entry.itemId, entry.subjectScope)
      const loaded = loadedElections[key]
      track('consent.secondary_decision', {
        item_id: entry.itemId,
        accepted: entry.election === 'opt_in',
        is_update: loaded === 'opt_in' || loaded === 'opt_out',
        ...(entry.subjectScope ? { subject_scope: entry.subjectScope } : {}),
      })
    }
  }

  async function saveAllChoices(
    items: readonly SecondaryConsentRow[],
    subjects: ConsentSubject[] | null,
    dualSecondary: boolean
  ): Promise<boolean> {
    if (!consentConfig) return false
    setSaving(true)
    try {
      const groups = items.map(({ item, sectionTitle }) => {
        const rows = subjects ? buildSubjectRows(item.id, subjects, dualSecondary) : null
        const entries = rows
          ? buildSubjectSecondaryElectionEntries(item, toggles, sectionTitle, rows)
          : buildSecondaryElectionEntries([item], toggles, sectionTitle, dualSecondary)
        return {
          itemId: item.id,
          rows,
          entries,
          documentContent: buildSecondaryDocumentContent(item, locale),
        }
      })
      if (groups.length === 0) return true

      const saved = await postSecondaryBatch(groups)
      if (!saved) return false

      for (const { itemId, entries, rows } of groups) {
        trackSavedItem(itemId, entries, rows)
      }
      return true
    } finally {
      setSaving(false)
    }
  }

  return {
    toggles,
    setToggles,
    standingPersonSelections,
    saving,
    saveAllChoices,
  }
}
